import React, { useContext, useEffect } from 'react';

import { Language, Locale } from '@rbilabs/intl';
import { noop } from 'lodash';
import { IntlProvider as ReactIntlProvider } from 'react-intl';

import { SupportedLanguages, SupportedRegions } from '@rbi-ctg/frontend';
import { IUserCurrentLocaleCache } from 'state/intl/types';
import { TLocalizationMessages } from 'types/i18n';
import { isTest } from 'utils/environment';
import { ISOs } from 'utils/form/constants';
import { TFormatDateFn } from 'utils/i18n';
import { TFormatDistanceToNowFn } from 'utils/i18n/distance';
import { TFormatDurationFn } from 'utils/i18n/duration';

import useIntlHook from './hook';
import { polyfillListFormatLocale } from './polyfill-list-format-locale';

export const defaultLocale = 'en-US';

export interface IIntlProviderContext {
  region: SupportedRegions;
  feCountryCode: ISOs;
  language: SupportedLanguages;
  locale: string;
  findSupportedRegion: Function;
  findSupportedLanguage: Function;
  setCurrentLocale: Function;
  clearRegionSpecificStorage: Function;
  messages: TLocalizationMessages;
  formatDate: TFormatDateFn;
  formatDistanceToNow: TFormatDistanceToNowFn;
  formatDuration: TFormatDurationFn;
  inferLanguage(): Promise<Language>;
  setUserCurrentLocale(userCurrentLocale: IUserCurrentLocaleCache | undefined): void;
  userCurrentLocale: IUserCurrentLocaleCache | undefined;
}

export const IntlContext = React.createContext<IIntlProviderContext>({} as IIntlProviderContext);

export const useLocale = () => useContext(IntlContext);

// NOTE: cypress-v2 test suite requirement
// Skip react-intl error logging when running in cypress
const skipErrorLogging = Boolean(isTest || window.Cypress);

export function IntlProvider({ children }: { children: React.ReactNode }) {
  const value = useIntlHook();
  useEffect(() => {
    polyfillListFormatLocale(value.locale as Locale);
  }, [value.locale]);
  return (
    <IntlContext.Provider value={value}>
      <ReactIntlProvider
        defaultLocale={defaultLocale}
        locale={value.locale}
        messages={value.messages}
        {...(skipErrorLogging && { onError: noop })}
      >
        {children}
      </ReactIntlProvider>
    </IntlContext.Provider>
  );
}

export default IntlContext.Consumer;
